import React, { useCallback, useState, useEffect } from 'react';
import {
    ActivityData,
    CommercialPrivateCustomer,
} from '../../../../interfaces/activity';
import {
    getSelectedActivity,
    selectIsLoading,
    selectSelectedActivity,
    updateSelectedActivity,
} from '../../../../store/slices/selectedActivitySlice';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, Form } from '@amzn/awsui-components-react-v2';
import { useNotifications } from '../../../../../common/context/grimsbyNotifications';
import { CustomerFormSection } from './CustomerFormSection';
import useFormValidation from '../../../../../common/utils/formValidation';
import { saveCustomerValidationConfig } from '../../../../services/activity-service';
import { DEFAULT_LANGUAGE, DEFAULT_CURRENCY } from '../../../../constants';
import {
    isSemiPrivateActivity,
    validateClassSizeConsistency,
} from '../../Common/Common';
import { validateClassroomCommitmentsCap } from '../../Common/classroomEligibilityRules';
import { handleScrollToError } from '../../../../../common/utils/scrollToError';

const CustomerCreateForm = () => {
    const isLoading = useSelector(selectIsLoading);
    const match = useRouteMatch<{ id: string }>();
    const history = useHistory();
    const dispatch = useDispatch();
    const activity: ActivityData | undefined = useSelector(
        selectSelectedActivity,
    )?.activity;
    const { addNotification } = useNotifications();
    const [classSizeValidationError, setClassSizeValidationError] =
        useState<string>('');
    const initialFormState = {
        customer_name: '',
        customer_aws_account_id: null,
        delivery_contact_name: null,
        delivery_contact_email: null,
        delivery_contact_phone_number: null,
        revenues: [],
        sfdc_opportunity_id: null,
        sfdc_account_id: null,
        organization_id: null,
        currency: DEFAULT_CURRENCY,
        tof_expiration_date: null,
        tof_status: null,
        customer_communication_language: DEFAULT_LANGUAGE,
        email_sent: false,
        notify_customer_date: null,
        most_recent_email_date: null,
        do_not_group_email: false,
        send_to_customer_poc: true,
    } as CommercialPrivateCustomer;

    const [formValues, setFormValues] =
        useState<CommercialPrivateCustomer>(initialFormState);

    const { errors, validateForm } =
        useFormValidation<CommercialPrivateCustomer>();

    const handleFieldEvent = useCallback(
        (changes: Partial<CommercialPrivateCustomer>) => {
            // Clear class size validation error when user modifies the field
            if ('number_of_students_committed' in changes) {
                setClassSizeValidationError('');
            }
            setFormValues((values) => ({
                ...values,
                ...changes,
            }));
        },
        [],
    );

    useEffect(() => {
        dispatch(getSelectedActivity(match.params.id));
    }, [dispatch, match.params.id]);

    const handleCreateCustomer = async () => {
        if (!!activity) {
            const invalid = validateForm(
                formValues,
                saveCustomerValidationConfig,
            );

            if (!invalid) {
                let updatedCustomers: Array<CommercialPrivateCustomer> = [];

                const newCustomer: CommercialPrivateCustomer = {
                    ...formValues,
                };

                if (!activity.customers) {
                    updatedCustomers = [newCustomer];
                } else {
                    updatedCustomers = [
                        ...activity.customers,
                        ...[newCustomer],
                    ];
                }

                // For semi-private activities, auto-update class_size to match sum of customer commitments
                const updatedActivity = {
                    ...activity,
                    customers: updatedCustomers,
                };
                const validation =
                    validateClassSizeConsistency(updatedActivity);
                // Auto-sync class_size for semi-private activities
                if (isSemiPrivateActivity(activity)) {
                    updatedActivity.class_size = validation.sum;
                }

                const commitmentsCapError = validateClassroomCommitmentsCap(
                    activity,
                    updatedCustomers,
                );
                if (commitmentsCapError) {
                    setClassSizeValidationError(commitmentsCapError);
                    setTimeout(() => handleScrollToError(), 0);
                    return;
                }

                const result = await dispatch<any>(
                    updateSelectedActivity(match.params.id, updatedActivity),
                );

                addNotification({
                    id: `edit-activity-customer-${Date.now()}`,
                    ...(result.isSuccessful
                        ? {
                              type: 'success',
                              content: 'The customer has been saved.',
                          }
                        : {
                              type: 'error',
                              content:
                                  'An error occurred while saving the customer.',
                          }),
                });

                if (result.isSuccessful) {
                    history.push({
                        pathname: `/activities/${match.params.id}`,
                        search: 'tabId=customers',
                    });
                }
            }
        }
    };

    return (
        <section data-testid="CustomerCreateForm">
            <Form
                header="Add customer"
                actions={
                    <div className="awsui-util-action-stripe awsui-util-mb-m">
                        <div className="awsui-util-action-stripe-group">
                            <Button
                                variant="link"
                                className="admin-customer-cancel"
                                data-testid="CustomerCreateFormCancel"
                                onClick={() => {
                                    history.push({
                                        pathname: `/activities/${match.params.id}`,
                                        search: 'tabId=customers',
                                    });
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="admin-customer-save"
                                data-testid="CustomerCreateFormAddButton"
                                loading={isLoading}
                                onClick={() => {
                                    handleCreateCustomer();
                                }}
                            >
                                {`${isLoading ? 'Saving' : 'Save'}`}
                            </Button>
                        </div>
                    </div>
                }
            >
                <CustomerFormSection
                    formValues={formValues}
                    errors={{
                        ...errors,
                        number_of_students_committed:
                            classSizeValidationError ||
                            errors?.number_of_students_committed,
                    }}
                    handleFieldEvent={handleFieldEvent}
                    activity={activity}
                />
            </Form>
        </section>
    );
};

export default CustomerCreateForm;
